import { api, setAuthentication } from './api';

/**
 * Servicio para manejar las gráficas Sankey
 * Basado en las especificaciones del documento ANALISIS_GRAFICAS_SANKEY.md
 */

/**
 * Obtiene datos de gráfica Sankey por proyecto
 * @param {string} proyectoId - ID del proyecto
 * @param {Object} params - Parámetros opcionales
 * @param {number} params.limite - Número de registros por sensor (default: 60)
 * @param {string[]} params.unidades - Filtrar por unidades específicas
 * @param {boolean} params.incluirInactivos - Incluir dispositivos inactivos (default: false)
 * @returns {Promise} Respuesta con datos de Sankey
 */
export const getSankeyByProyecto = async (proyectoId, params = {}) => {
  try {
    setAuthentication();
    const queryParams = new URLSearchParams();
    
    if (params.limite) queryParams.append('limite', params.limite);
    if (params.unidades && params.unidades.length > 0) {
      queryParams.append('unidades', params.unidades.join(','));
    }
    if (params.incluirInactivos !== undefined) {
      queryParams.append('incluirInactivos', params.incluirInactivos);
    }

    const url = `/sankey/proyecto/${proyectoId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener gráfica Sankey por proyecto:', error);
    throw error;
  }
};

/**
 * Obtiene datos de gráfica Sankey por elemento
 * @param {string} elementoId - ID del elemento
 * @param {Object} params - Parámetros opcionales (mismos que proyecto)
 * @returns {Promise} Respuesta con datos de Sankey
 */
export const getSankeyByElemento = async (elementoId, params = {}) => {
  try {
    setAuthentication();
    const queryParams = new URLSearchParams();
    
    if (params.limite) queryParams.append('limite', params.limite);
    if (params.unidades && params.unidades.length > 0) {
      queryParams.append('unidades', params.unidades.join(','));
    }
    if (params.incluirInactivos !== undefined) {
      queryParams.append('incluirInactivos', params.incluirInactivos);
    }

    const url = `/sankey/elemento/${elementoId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener gráfica Sankey por elemento:', error);
    throw error;
  }
};

/**
 * Obtiene datos de gráfica Sankey por tarjeta
 * @param {string} tarjetaId - ID de la tarjeta
 * @param {Object} params - Parámetros opcionales (mismos que proyecto)
 * @returns {Promise} Respuesta con datos de Sankey
 */
export const getSankeyByTarjeta = async (tarjetaId, params = {}) => {
  try {
    setAuthentication();
    const queryParams = new URLSearchParams();
    
    if (params.limite) queryParams.append('limite', params.limite);
    if (params.unidades && params.unidades.length > 0) {
      queryParams.append('unidades', params.unidades.join(','));
    }
    if (params.incluirInactivos !== undefined) {
      queryParams.append('incluirInactivos', params.incluirInactivos);
    }

    const url = `/sankey/tarjeta/${tarjetaId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener gráfica Sankey por tarjeta:', error);
    throw error;
  }
};

/**
 * Transforma los datos de la nueva API al formato esperado por el componente Sankey existente
 * @param {Object} sankeyData - Datos de la nueva API
 * @returns {Object} Datos transformados para el componente
 */
export const transformSankeyData = (sankeyData) => {
  if (!sankeyData || !sankeyData.nodes || !sankeyData.links) {
    return null;
  }

  // Crear un mapa de nodos por ID para fácil acceso
  const nodeMap = {};
  sankeyData.nodes.forEach(node => {
    nodeMap[node.id] = node;
  });

  // Transformar los datos al formato esperado por Google Charts
  const chartData = [['From', 'To', 'Weight']];
  
  sankeyData.links.forEach(link => {
    const sourceNode = nodeMap[link.source];
    const targetNode = nodeMap[link.target];
    
    if (sourceNode && targetNode) {
      const sourceName = `${sourceNode.name} (${sourceNode.value}${sourceNode.unidades ? ' ' + sourceNode.unidades : ''})`;
      const targetName = `${targetNode.name} (${targetNode.value}${targetNode.unidades ? ' ' + targetNode.unidades : ''})`;
      
      chartData.push([sourceName, targetName, link.value]);
    }
  });

  return {
    chartData,
    metadata: sankeyData.metadata,
    nodes: sankeyData.nodes,
    links: sankeyData.links
  };
};